import { inject, Injectable, Renderer2 } from '@angular/core';
import { IPayout, ManageableStorageLocation } from '@luggagehero/shared/interfaces';
import { SharedPricingService } from '@luggagehero/shared/services/pricing';
import { SharedScriptService } from '@luggagehero/shared/services/script';
import { SharedTranslateService } from '@luggagehero/shared/services/translation';
import moment from 'moment';
import { mergeMap } from 'rxjs/operators';

import { InvoiceServiceStrategy } from './invoice-service.strategy';
import { exportPdf } from './pdf-export.util';

interface Company {
  name: string;
  address: string;
  vatNumber?: string;
}

// TODO: Move values to config and get from there
const DK_COMPANY: Company = {
  name: `LuggageHero A/S`,
  address: 'Aarhusgade 118, 2150 Nordhavn, Denmark',
  vatNumber: 'DK37611328',
};
const US_COMPANY: Company = {
  name: `LuggageHero, LLC`,
  address: '228 Park Ave S # 300, New York, NY 10003, USA',
};
const DATE_FORMAT = 'YYYYMMDD';
const LOGO_URL = 'assets/logo_light_bg.png';

const WalkInBonusName = 'WalkInBonus';
const WalkInBonusDesription = 'Walk-in Bonus';

@Injectable({
  providedIn: 'root',
})
export class SharedInvoiceV2Service implements InvoiceServiceStrategy {
  private priceService = inject(SharedPricingService);
  private script = inject(SharedScriptService);
  private translate = inject(SharedTranslateService);

  private logoDataURL: string | undefined;
  private isInitialized = false;

  public get isReady(): boolean {
    return this.logoDataURL ? true : false;
  }

  public init(renderer: Renderer2, _doc: unknown) {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    // Load logo as data URL
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = <HTMLCanvasElement>renderer.createElement('CANVAS');
      canvas.height = img.height;
      canvas.width = img.width;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
      }

      this.logoDataURL = canvas.toDataURL('image/png');
    };
    img.src = LOGO_URL;

    // Load pdfmake first and then vfs_fonts
    this.script
      .load('pdfmake')
      .pipe(mergeMap((_res) => this.script.load('vfs_fonts')))
      .subscribe();
  }

  public open(payout: IPayout, shop: ManageableStorageLocation) {
    exportPdf(this.getDocDefinition(payout, shop), 'open');
  }

  public print(payout: IPayout, shop: ManageableStorageLocation) {
    exportPdf(this.getDocDefinition(payout, shop), 'print');
  }

  public download(payout: IPayout, shop: ManageableStorageLocation) {
    exportPdf(this.getDocDefinition(payout, shop), 'download');
  }

  public getDocDefinition(
    payout: IPayout,
    shop: ManageableStorageLocation,
  ): {
    info: Record<string, string>;
    content: unknown;
    pageSize: string;
  } {
    return {
      info: this.getDocInfo(payout, shop),
      content: this.getDocContent(payout, shop),
      pageSize: 'A4',
    };
  }

  public getDocInfo(payout: IPayout, _shop: ManageableStorageLocation): Record<string, string> {
    const date = moment(payout.created).format('YYYY-MM-DD');
    return {
      title: `[${date}] LuggageHero - invoice - ${payout.invoiceNumber}`,
      author: 'LuggageHero',
      creator: 'LuggageHero',
      producer: 'LuggageHero',
      subject: `LuggageHero invoice ${payout.invoiceNumber}`,
      keywords: `LuggageHero invoice ${payout.invoiceNumber}`,
    };
  }

  public getDocContent(payout: IPayout, shop: ManageableStorageLocation): unknown {
    return [
      this.getHeader(payout, shop),
      this.getSales(payout, shop),
      this.getBalance(payout, shop),
      this.getFooter(payout, shop),
    ];
  }

  public getHeader(payout: IPayout, shop: ManageableStorageLocation): Array<Record<string, unknown>> {
    return [
      {
        text: this.translate.instant('INVOICE_V2_TITLE'),
        fontSize: 22,
        bold: true,
        color: '#1a1a1a',
        characterSpacing: 2,
        margin: [0, 0, 0, 16],
      },
      {
        image: this.logoDataURL,
        width: 120,
        alignment: 'right',
      },
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: `${this.translate.instant('INVOICE_V2_BILL_TO')}:`, bold: true, margin: [0, 0, 0, 4] },
              { text: shop.name },
              ...(shop.vatNumber
                ? [
                    {
                      text: `${this.translate.instant('INVOICE_V2_VAT_NUMBER_LABEL')}: ${shop.vatNumber.toUpperCase()}`,
                      color: '#666',
                    },
                  ]
                : []),
              { text: this.getShopAddress(shop), color: '#666' },
            ],
          },
          {
            width: 'auto',
            layout: 'noBorders',
            table: {
              body: [
                [
                  { text: `${this.translate.instant('INVOICE_V2_NUMBER_LABEL')}:`, alignment: 'right' },
                  { text: payout.invoiceNumber, alignment: 'right' },
                ],
                [
                  { text: `${this.translate.instant('INVOICE_V2_DATE_LABEL')}:`, alignment: 'right' },
                  { text: moment(payout.created).format('L'), alignment: 'right' },
                ],
              ],
            },
          },
        ],
        margin: [0, 24, 0, 0],
      },
    ];
  }

  public getSales(payout: IPayout, shop: ManageableStorageLocation): unknown {
    const body = [];
    const showVat = !shop.region.match(/us/i);
    const commission = payout.data.commission ?? 0;
    const totalServiceFees = payout.data.totalServiceFees ?? 0;
    const subTotal = payout.data.subTotal ?? 0;
    const vat = payout.data.vat ?? 0;
    const totalInclVat = subTotal + vat;

    body.push([
      this.translate.instant('INVOICE_V2_COMMISSION'),
      { text: this.formatPrice(commission, payout.currency), alignment: 'right' },
    ]);

    if (totalServiceFees > 0) {
      body.push([
        this.translate.instant('INVOICE_V2_SERVICE_FEES'),
        { text: this.formatPrice(totalServiceFees, payout.currency), alignment: 'right' },
      ]);
    }

    const minRevenueAdj = payout.data.minimumRevenueAdjustmentConverted;
    if (minRevenueAdj != null && minRevenueAdj < 0) {
      body.push([
        this.translate.instant('INVOICE_V2_MINIMUM_REVENUE_GUARANTEE'),
        { text: this.formatPrice(minRevenueAdj, payout.currency), alignment: 'right' },
      ]);
    }

    // Add a line for each non-bonus transaction, then a single rolled-up walk-in-bonus line
    if (payout.data.transactions) {
      payout.data.transactions
        .filter((t) => t.description !== WalkInBonusDesription)
        .forEach((t) => {
          const sign = t.amount < 0 ? '' : '- '; // flip the sign to minus if positive value
          body.push([
            t.description,
            { text: `${sign} ${this.formatPrice(t.amount, payout.currency)}`, alignment: 'right' },
          ]);
        });

      const walkInBonuses = payout.data.transactions.filter((t) => {
        const meta = t.metadata as { bonusName?: string } | undefined;
        return meta?.bonusName === WalkInBonusName || t.description === WalkInBonusDesription;
      });

      if (walkInBonuses.length > 0) {
        const totalBonusAmount = walkInBonuses.reduce((sum, t) => sum + t.amount, 0);
        const description =
          walkInBonuses.length > 1
            ? `${walkInBonuses.length}x ${walkInBonuses[0].description}`
            : walkInBonuses[0].description;

        body.push([
          description,
          { text: `- ${this.formatPrice(totalBonusAmount, payout.currency)}`, alignment: 'right' },
        ]);
      }
    }

    if (showVat) {
      // VAT-region totals: three explicit rows so the breakdown is unambiguous.
      body.push([
        { text: this.translate.instant('INVOICE_V2_TOTAL_EXCL_VAT'), bold: true },
        { text: this.formatPrice(subTotal, payout.currency), bold: true, alignment: 'right' },
      ]);
      body.push([
        this.translate.instant('INVOICE_V2_VAT', undefined, {
          percent: this.formatPercentage(vat, subTotal),
        }),
        { text: this.formatPrice(vat, payout.currency), alignment: 'right' },
      ]);
      body.push([
        { text: this.translate.instant('INVOICE_V2_TOTAL_INCL_VAT'), bold: true, fillColor: '#f6f6f6' },
        {
          text: this.formatPrice(totalInclVat, payout.currency),
          bold: true,
          alignment: 'right',
          fillColor: '#f6f6f6',
        },
      ]);
    } else {
      // Non-VAT region (e.g. US): single Total row.
      body.push([
        { text: this.translate.instant('INVOICE_V2_TOTAL'), bold: true, fillColor: '#f6f6f6' },
        {
          text: this.formatPrice(totalInclVat, payout.currency),
          bold: true,
          alignment: 'right',
          fillColor: '#f6f6f6',
        },
      ]);
    }

    const totalRevenueFormatted = this.formatPrice(payout.data.totalRevenue ?? 0, payout.currency);
    const totalTipsConverted = payout.data.totalTipsConverted ?? 0;
    const summaryKey = totalTipsConverted > 0 ? 'INVOICE_V2_SALES_SUMMARY_WITH_TIPS' : 'INVOICE_V2_SALES_SUMMARY';
    const summaryParams =
      totalTipsConverted > 0
        ? { amount: totalRevenueFormatted, tips: this.formatPrice(totalTipsConverted, payout.currency) }
        : { amount: totalRevenueFormatted };

    return [
      {
        text: this.translate.instant(summaryKey, undefined, summaryParams),
        bold: true,
        margin: [0, 48, 0, 0],
      },
      {
        text: this.translate.instant('INVOICE_V2_PERIOD_LINE', undefined, { period: this.getPeriod(payout) }),
        color: '#666',
        margin: [0, 2, 0, 0],
      },
      {
        text: this.translate.instant('INVOICE_V2_FEES_HEADING'),
        bold: true,
        margin: [0, 32, 0, 0],
      },
      this.getHorizontalLine(),
      {
        layout: 'noBorders',
        table: {
          widths: ['*', 'auto'],
          body: body,
        },
      },
      this.getHorizontalLine(),
    ];
  }

  public getBalance(payout: IPayout, shop: ManageableStorageLocation): unknown {
    const showVat = !shop.region.match(/us/i);
    const body = [];

    body.push([
      this.translate.instant('INVOICE_V2_TOTAL_SALES'),
      { text: this.formatPrice(payout.data.totalRevenue ?? 0, payout.currency), alignment: 'right' },
    ]);

    if ((payout.data.inShopRevenue ?? 0) > 0 || (payout.data.paidDirectly ?? 0) > 0) {
      let inShopRevenue = payout.data.inShopRevenue ?? 0;
      if (payout.data.paidDirectly && payout.data.paidDirectly > 0) {
        // inShopRevenue is the old field (storage amount only); paidDirectly is the new field
        // (storage amount + other services, and includes tips — so subtract those back out).
        inShopRevenue = payout.data.paidDirectly;
        if ((payout.data.inShopTips ?? 0) > 0) {
          inShopRevenue = inShopRevenue - (payout.data.inShopTips ?? 0);
        }
      }

      body.push([
        this.translate.instant('INVOICE_V2_RECEIVED_DIRECTLY'),
        { text: `- ${this.formatPrice(inShopRevenue, payout.currency)}`, alignment: 'right' },
      ]);
    }

    const totalCostOfService = (payout.data.subTotal ?? 0) + (payout.data.vat ?? 0);
    const luggageHeroFeesKey = showVat ? 'INVOICE_V2_LUGGAGEHERO_FEES_INCL_VAT' : 'INVOICE_V2_LUGGAGEHERO_FEES';
    body.push([
      this.translate.instant(luggageHeroFeesKey),
      { text: `- ${this.formatPrice(totalCostOfService, payout.currency)}`, alignment: 'right' },
    ]);

    if ((payout.data.totalTipsConverted ?? 0) > 0) {
      if ((payout.data.inShopTipsConverted ?? 0) > 0) {
        body.push([
          this.translate.instant('INVOICE_V2_IN_SHOP_TIPS'),
          {
            text: this.formatPrice((payout.data.inShopTipsConverted ?? 0) * -1, payout.currency),
            alignment: 'right',
          },
        ]);
      }

      body.push([
        { text: this.translate.instant('INVOICE_V2_TOTAL_TIPS') },
        {
          text: this.formatPrice(payout.data.totalTipsConverted ?? 0, payout.currency),
          alignment: 'right',
        },
      ]);
    }

    if (payout.data.previousBalance) {
      body.push([
        this.translate.instant('INVOICE_V2_PREVIOUS_BALANCE'),
        { text: this.formatPrice(payout.data.previousBalance, payout.currency), alignment: 'right' },
      ]);
    }

    const balance = payout.data.balance ?? 0;
    const balanceLabelKey = balance >= 0 ? 'INVOICE_V2_BALANCE_IN_YOUR_FAVOR' : 'INVOICE_V2_BALANCE_OWED';
    body.push([
      { text: this.translate.instant(balanceLabelKey), bold: true, fillColor: '#f6f6f6' },
      {
        text: this.formatPrice(balance, payout.currency),
        bold: true,
        alignment: 'right',
        fillColor: '#f6f6f6',
      },
    ]);

    if (payout.data.taxes != null && payout.data.taxes > 0) {
      body.push([
        this.translate.instant('INVOICE_V2_UNDEDUCTED_TAX'),
        { text: `(${this.formatPrice(payout.data.taxes, payout.currency)})`, alignment: 'right' },
      ]);
    }

    let footer = '';
    if (balance > 0) {
      const fundsAvailableDate = moment(payout.created).add(8, 'days').format('L');
      footer = this.translate.instant('INVOICE_V2_FUNDS_AVAILABLE_ON', undefined, { date: fundsAvailableDate });
    } else if (balance < 0) {
      footer = this.translate.instant('INVOICE_V2_BALANCE_CARRIED_OVER');
    }

    return [
      {
        text: `${this.translate.instant('INVOICE_V2_BALANCE_HEADING')}:`,
        bold: true,
        margin: [0, 24, 0, 0],
      },
      this.getHorizontalLine(),
      {
        layout: 'noBorders',
        table: {
          widths: ['*', 'auto'],
          body: body,
        },
      },
      this.getHorizontalLine(),
      { text: footer, fontSize: 10, color: '#666', italics: true, margin: [0, 4, 0, 0] },
    ];
  }

  public getFooter(_payout: IPayout, shop: ManageableStorageLocation): unknown {
    // TODO: this should be based on the LuggageHero Stripe account used for the payout (DK/US)
    const company = shop.payoutOrigin === 'us' ? US_COMPANY : DK_COMPANY;
    let text = `${company.name} | ${company.address}`;
    if (company.vatNumber) {
      text += ` | ${this.translate.instant('INVOICE_V2_VAT_NUMBER_LABEL')}: ${company.vatNumber}`;
    }
    return {
      text: text,
      alignment: 'center',
      fontSize: 8,
      color: '#888',
      margin: [0, 48, 0, 0],
    };
  }

  public getHorizontalLine(): unknown {
    // A4 content width: 595pt page width − 40pt + 40pt pdfMake default margins = 515pt.
    return {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#d0d0d0' }],
      margin: [0, 12, 0, 12],
    };
  }

  public getShopAddress(shop: ManageableStorageLocation): string {
    return shop.address.formattedAddress.replace('Storbritannien', 'United Kingdom');
  }

  public getPeriod(payout: IPayout) {
    const dateLength = DATE_FORMAT.length;
    const keyParts = payout.key.split('-');
    if (keyParts.length !== 2) {
      return payout.key;
    }
    let from = keyParts[0],
      to = keyParts[1];
    if (from.length !== dateLength || to.length !== dateLength) {
      return payout.key;
    }
    from = moment(from, DATE_FORMAT).format('L');
    to = moment(to, DATE_FORMAT).format('L');
    return `${from} - ${to}`;
  }

  public formatPercentage(dividend: number | undefined, divisor: number): string {
    if (!divisor) {
      return '0%';
    }
    const percentage = ((dividend ?? 0) / divisor) * 100;
    if (!isFinite(percentage)) {
      return '0%';
    }
    return `${Math.round(percentage).toFixed(0)}%`;
  }

  public formatPrice(price: number, currency: string, removeZeroDecimals = false) {
    return this.priceService.format(price, currency, 2, removeZeroDecimals);
  }
}
